import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://thirdcultureleadership.com';

/**
 * JSON-LD Structured Data Component
 * 
 * Adds Schema.org structured data to pages for enhanced SEO
 * and rich snippets in search results.
 */

// Person schema for Dr Promise Monday
export const PersonSchema = () => {
  const personData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Dr Promise Monday",
    "alternateName": "Promise Monday",
    "jobTitle": ["Specialty Doctor in Paediatrics", "Author", "Keynote Speaker"],
    "description": "Paediatrician, Author, and founder of Third Culture Leadership. Expert in cultural fluency, cross-cultural leadership, and diversity strategy.",
    "affiliation": {
      "@type": "Organization",
      "name": "Shrewsbury and Telford Hospital NHS Trust",
      "url": "https://www.sath.nhs.uk/"
    },
    "worksFor": {
      "@type": "Organization",
      "name": "Third Culture Leadership",
      "url": SITE_URL
    },
    "knowsAbout": [
      "Cultural Fluency",
      "Cross-Cultural Leadership",
      "Diversity and Inclusion",
      "Paediatric Medicine",
      "Keynote Speaking"
    ],
    "url": `${SITE_URL}/about`,
    "image": "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/xa54zl60_A%20Third%20Culture%20is%20Possible%20.webp"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(personData)}
      </script>
    </Helmet>
  );
};

// Organization schema for Third Culture Leadership
export const OrganizationSchema = () => {
  const orgData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Third Culture Leadership",
    "alternateName": "TCL",
    "url": SITE_URL,
    "logo": `${SITE_URL}/tcl-icon.svg`,
    "description": "Third Culture Leadership equips leaders to navigate cultural complexity, communicate across difference, and build trusted relationships — without compromising identity, values, or integrity.",
    "founder": {
      "@type": "Person",
      "name": "Dr Promise Monday"
    },
    "foundingDate": "2024",
    "slogan": "Lead Across Cultures Without Losing Who You Are.",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "url": `${SITE_URL}/contact`
    },
    "areaServed": "Worldwide",
    "serviceType": [
      "Leadership Training",
      "Keynote Speaking",
      "Cultural Fluency Consulting",
      "Executive Coaching"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(orgData)}
      </script>
    </Helmet>
  );
};

// WebSite schema with search action
export const WebSiteSchema = () => {
  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Third Culture Leadership",
    "url": SITE_URL,
    "description": "Lead Across Cultures Without Losing Who You Are. Resources and programmes for leaders navigating cultural complexity and building trusted relationships across difference.",
    "publisher": {
      "@type": "Organization",
      "name": "Third Culture Leadership"
    },
    "inLanguage": "en-GB"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteData)}
      </script>
    </Helmet>
  );
};

// Book schema for Cultural Fluency Without Compromise
export const BookSchema = () => {
  const bookData = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": "Cultural Fluency Without Compromise",
    "alternateName": "CFWC",
    "description": "Thriving Across Cultures with Presence and Integrity. A guide to navigating multicultural professional environments while maintaining your authentic identity.",
    "author": {
      "@type": "Person",
      "name": "Dr Promise Monday",
      "url": `${SITE_URL}/about`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Promond Publishing",
      "url": `${SITE_URL}/promond-publishing`
    },
    "datePublished": "2025",
    "isbn": "978-1-918265-02-6",
    "bookFormat": "EBook",
    "numberOfPages": "200",
    "inLanguage": "en",
    "genre": ["Business", "Leadership", "Self-Help", "Cultural Studies"],
    "image": "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/xa54zl60_A%20Third%20Culture%20is%20Possible%20.webp",
    "url": `${SITE_URL}/books`,
    "offers": {
      "@type": "Offer",
      "price": "9.99",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock",
      "url": `${SITE_URL}/books`
    },
    "about": [
      {
        "@type": "Thing",
        "name": "Cultural Fluency"
      },
      {
        "@type": "Thing",
        "name": "Cross-Cultural Leadership"
      },
      {
        "@type": "Thing",
        "name": "Professional Development"
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(bookData)}
      </script>
    </Helmet>
  );
};

// Course schema for Self-Paced Learning
export const CourseSchema = () => {
  const courseData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Third Culture Leadership — Self-Paced Learning",
    "description": "Master cultural fluency with our comprehensive self-paced course. Learn to lead across cultures with confidence and integrity.",
    "provider": {
      "@type": "Organization",
      "name": "Third Culture Leadership",
      "url": SITE_URL
    },
    "instructor": {
      "@type": "Person",
      "name": "Dr Promise Monday"
    },
    "url": `${SITE_URL}/courses`,
    "image": "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/xa54zl60_A%20Third%20Culture%20is%20Possible%20.webp",
    "educationalLevel": "Professional",
    "courseMode": "Online",
    "isAccessibleForFree": false,
    "offers": {
      "@type": "Offer",
      "price": "10.00",
      "priceCurrency": "GBP",
      "category": "per module"
    },
    "hasCourseInstance": [
      {
        "@type": "CourseInstance",
        "courseMode": "Online",
        "courseWorkload": "PT4H"
      }
    ],
    "about": [
      "Cultural Fluency",
      "Cross-Cultural Leadership",
      "Professional Identity",
      "Adaptive Communication"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(courseData)}
      </script>
    </Helmet>
  );
};

// Service schema for Speaking/Keynotes
export const SpeakingServiceSchema = () => {
  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Keynote Speaking & Workshops",
    "description": "Book Dr Promise Monday for keynotes and workshops on cultural fluency, inclusive leadership, and thriving across cultures.",
    "provider": {
      "@type": "Person",
      "name": "Dr Promise Monday",
      "url": `${SITE_URL}/about`
    },
    "serviceType": ["Keynote Speaking", "Workshop Facilitation", "Corporate Training"],
    "areaServed": "Worldwide",
    "url": `${SITE_URL}/speaking`,
    "image": "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/xa54zl60_A%20Third%20Culture%20is%20Possible%20.webp",
    "offers": {
      "@type": "Offer",
      "description": "Speaking engagements available for conferences, corporate events, and educational institutions"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": ["Healthcare Organizations", "Corporations", "Educational Institutions", "Professional Conferences"]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(serviceData)}
      </script>
    </Helmet>
  );
};

// FAQ Schema for common questions
export const FAQSchema = ({ faqs }) => {
  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList schema
export const BreadcrumbSchema = ({ items }) => {
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};

// Combined homepage schema
export const HomepageSchemas = () => (
  <>
    <PersonSchema />
    <OrganizationSchema />
    <WebSiteSchema />
  </>
);

export default {
  PersonSchema,
  OrganizationSchema,
  WebSiteSchema,
  BookSchema,
  CourseSchema,
  SpeakingServiceSchema,
  FAQSchema,
  BreadcrumbSchema,
  HomepageSchemas
};
